import { useState, useEffect, useRef, useCallback } from 'react';
import websocketService from '../services/websocketService';

/**
 * Custom hook for managing WebSocket connections for duel-related data
 * Provides trade updates for both challenger and opponent accounts
 *
 * @param {Object} duel - The normalized duel data
 * @param {Function} onTradesUpdate - Callback function to handle trade updates (receives trades array and account info)
 * @returns {Object} - Hook state and utilities
 */
export const useDuelWebSocket = (duel, onTradesUpdate) => {
  const [trades, setTrades] = useState([]);
  const subscribedAccounts = useRef([]);
  const accountIndices = useRef({ challenger: null, opponent: null });

  // Process WebSocket messages and extract trade data
  const handleWebSocketMessage = useCallback((message) => {
    try {
      // Check if this is an account_all update message
      if (message.type === "update/account_all" && message.trades) {
        console.log('Received WebSocket message:', message);

        const accountIndex = message.account;
        const challengerAccount = accountIndices.current.challenger;
        const isChallengerTrade =
          challengerAccount && accountIndex === challengerAccount;

        // Extract trades from all markets (keys like "0", "1", "2", etc.)
        const allTrades = [];
        Object.keys(message.trades).forEach((marketKey) => {
          const marketTrades = message.trades[marketKey] || [];
          // Filter only trades with type "trade" (not other types like "liquidation")
          const filteredTrades = marketTrades.filter(
            (trade) => trade.type === "trade"
          );
          allTrades.push(...filteredTrades);
        });

        if (allTrades.length > 0) {
          // Add account information to each trade for display purposes
          const challengerUsername = duel?.challengerUsername || "CHALLENGER";
          const opponentUsername = duel?.opponentUsername || "HOST";

          const tradesWithAccountInfo = allTrades.map((trade) => {
            const challengerAccount = accountIndices.current.challenger;
            const opponentAccount = accountIndices.current.opponent;

            const isChallengerAsking =
              challengerAccount && trade.ask_account_id === challengerAccount;
            const isChallengerBidding =
              challengerAccount && trade.bid_account_id === challengerAccount;
            const isOpponentAsking =
              opponentAccount && trade.ask_account_id === opponentAccount;
            const isOpponentBidding =
              opponentAccount && trade.bid_account_id === opponentAccount;

            let user = "";
            let side = "Trade";

            if (isChallengerAsking) {
              user = challengerUsername;
              side = "Short";
            } else if (isChallengerBidding) {
              user = challengerUsername;
              side = "Long";
            } else if (isOpponentAsking) {
              user = opponentUsername;
              side = "Short";
            } else if (isOpponentBidding) {
              user = opponentUsername;
              side = "Long";
            }

            return { ...trade, user, side };
          });

          // Sort trades by timestamp (newest first for display)
          const sortedTrades = tradesWithAccountInfo.sort((a, b) => {
            // Handle both millisecond and second timestamps
            const timestampA =
              a.timestamp > 10000000000 ? a.timestamp / 1000 : a.timestamp;
            const timestampB =
              b.timestamp > 10000000000 ? b.timestamp / 1000 : b.timestamp;
            return timestampB - timestampA; // Newest first for display
          });

          // Accumulate trades (add new ones, avoid duplicates)
          setTrades((prevTrades) => {
            const existingTradeIds = new Set(
              prevTrades.map((t) => t.trade_id)
            );
            const newTrades = sortedTrades.filter(
              (trade) => !existingTradeIds.has(trade.trade_id)
            );

            if (newTrades.length > 0) {
              const updatedTrades = [...newTrades, ...prevTrades];

              // Call the callback if provided with trade info and account type
              if (onTradesUpdate) {
                onTradesUpdate(updatedTrades, {
                  accountIndex,
                  isChallengerTrade,
                  accountType: isChallengerTrade ? 'challenger' : 'opponent'
                });
              }

              return updatedTrades;
            }
            return prevTrades;
          });
        }
      }
    } catch (error) {
      console.error("Error processing WebSocket message:", error);
    }
  }, [duel?.challengerUsername, duel?.opponentUsername, onTradesUpdate]);

  // Set up WebSocket subscriptions when duel data changes
  useEffect(() => {
    if (!duel) return;

    let accountsToSubscribe = [];
    let handleReconnection = null;

    // Subscribe to WebSocket channels for both users' accounts
    if (duel.challengerLighterAccountIndex) {
      websocketService.subscribeToAccount(duel.challengerLighterAccountIndex);
      accountsToSubscribe.push(duel.challengerLighterAccountIndex);
      accountIndices.current.challenger = duel.challengerLighterAccountIndex;
    }
    if (duel.opponentLighterAccountIndex) {
      websocketService.subscribeToAccount(duel.opponentLighterAccountIndex);
      accountsToSubscribe.push(duel.opponentLighterAccountIndex);
      accountIndices.current.opponent = duel.opponentLighterAccountIndex;
    }
    subscribedAccounts.current = accountsToSubscribe;

    // Set up reconnection handler
    handleReconnection = () => {
      if (accountsToSubscribe.length > 0) {
        console.log(
          "WebSocket reconnected, restoring subscriptions for accounts:",
          accountsToSubscribe
        );
        accountsToSubscribe.forEach((accountIndex) => {
          websocketService.subscribeToAccount(accountIndex);
        });
      }
    };

    websocketService.on("connected", handleReconnection);

    // Cleanup function to unsubscribe from WebSocket channels
    return () => {
      if (handleReconnection) {
        websocketService.off("connected", handleReconnection);
      }
      subscribedAccounts.current.forEach((accountIndex) => {
        websocketService.unsubscribe(`account_all/${accountIndex}`);
      });
      subscribedAccounts.current = [];
    };
  }, [duel]);

  // Set up WebSocket message listener
  useEffect(() => {
    websocketService.on("message", handleWebSocketMessage);

    // Cleanup
    return () => {
      websocketService.off("message", handleWebSocketMessage);
    };
  }, [handleWebSocketMessage]);

  return {
    trades,
    setTrades,
    subscribedAccounts: subscribedAccounts.current,
    accountIndices: accountIndices.current
  };
};
